import { useState, useEffect, useCallback } from 'react'
import { FolderOpen, Folder, FileVideo, FileImage, FileAudio, FileText, ExternalLink, AlertCircle } from 'lucide-react'
import { Button } from '@anyemp/ui-kit'
import { talentMedia } from '../../services/apiService'
import { Loader } from '../UI/LoadingSpinner'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const TYPE_ICONS = { video: FileVideo, image: FileImage, audio: FileAudio }

const formatSize = (bytes) => {
  if (!bytes) return null
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

const formatDuration = (seconds) => {
  if (!seconds) return null
  const s = Math.round(seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

const FileRow = ({ file }) => {
  const Icon = TYPE_ICONS[file.type] || FileText
  const meta = [file.type, formatDuration(file.duration), formatSize(file.size)].filter(Boolean).join(' · ')
  return (
    <a
      href={file.url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary"
    >
      {file.thumbnail_url
        ? <img src={file.thumbnail_url} alt="" className="h-9 w-14 shrink-0 rounded object-cover" loading="lazy" />
        : <Icon className="h-5 w-5 shrink-0 text-light-text-tertiary dark:text-dark-text-tertiary" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-light-text-primary dark:text-dark-text-primary">{file.title || file.name}</p>
        {meta && <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{meta}</p>}
      </div>
    </a>
  )
}

const FolderCard = ({ folder }) => (
  <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary p-3">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Folder className="h-4 w-4 shrink-0 text-primary-500" />
          {folder.url ? (
            <a
              href={folder.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in the media library"
              className="truncate text-sm font-medium text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 hover:underline"
            >
              {(folder.breadcrumb || []).join(' / ') || folder.path}
            </a>
          ) : (
            <p className="truncate text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {(folder.breadcrumb || []).join(' / ') || folder.path}
            </p>
          )}
        </div>
        <p className="mt-1 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
          {folder.reasons.map(r => (r.detail ? `${r.label}: ${r.detail}` : r.label)).join(' · ')}
        </p>
      </div>
      {folder.url && (
        <a
          href={folder.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-xs text-primary-500 hover:underline"
        >
          Open <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>

    {folder.files === null ? (
      <p className="mt-2 text-xs text-red-500 dark:text-red-400">Files could not be loaded</p>
    ) : folder.files && folder.files.length > 0 ? (
      <div className="mt-2 space-y-0.5">
        {folder.files.map(file => <FileRow key={file.id} file={file} />)}
        {folder.url && folder.files_total > folder.files.length && (
          <a href={folder.url} target="_blank" rel="noopener noreferrer" className="block px-2 pt-1 text-xs text-primary-500 hover:underline">
            {folder.files_total - folder.files.length} more in the media library
          </a>
        )}
      </div>
    ) : folder.files ? (
      <p className="mt-2 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
        No files{folder.subfolders?.length ? ` — ${folder.subfolders.length} subfolder(s)` : ''}
      </p>
    ) : null}
  </div>
)

const MediaLibrarySection = ({ talentId }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setData(await talentMedia.getMediaLibrary(talentId))
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Error loading the media library')
    } finally {
      setLoading(false)
    }
  }, [talentId])

  useEffect(() => { load() }, [load])

  const exact = (data?.folders || []).filter(f => f.match === 'exact')
  const possible = (data?.folders || []).filter(f => f.match === 'possible')

  return (
    <SectionBlock id="media-library">
      <SectionHeader icon={FolderOpen} title="Media Library" />

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader variant="component" size="md" fullHeight={false} />
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-10 w-10 text-red-400 dark:text-red-500 mb-3" />
          <p className="text-sm text-red-500 dark:text-red-400 mb-3">{error}</p>
          <Button size="small" variant="text" color="primary" onClick={load}>Try again</Button>
        </div>
      ) : data && !data.configured ? (
        <p className="py-6 text-center text-sm text-light-text-secondary dark:text-dark-text-secondary">
          The media library is not connected on this server
        </p>
      ) : exact.length === 0 && possible.length === 0 ? (
        <div className="text-center py-8">
          <FolderOpen className="mx-auto h-10 w-10 text-light-text-tertiary dark:text-dark-text-tertiary mb-3" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No folders found for this talent</p>
        </div>
      ) : (
        <div className="space-y-5">
          {exact.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary">Folders of this talent</p>
              {exact.map(folder => <FolderCard key={folder.id} folder={folder} />)}
            </div>
          )}
          {possible.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary">Possibly this talent (matched by name)</p>
              {possible.map(folder => <FolderCard key={folder.id} folder={folder} />)}
            </div>
          )}
        </div>
      )}
    </SectionBlock>
  )
}

export default MediaLibrarySection
